import { useState, useCallback, useMemo } from "react";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import TuneIcon from "@mui/icons-material/Tune";
import BottomSheet from "components/common/BottomSheet";
import Tab from "components/Tab";
import "./ProductFilter.scss";

const SORT_OPTIONS = [
    { value: "recommended", label: "추천순" },
    { value: "latest", label: "최신순" },
    { value: "price_asc", label: "낮은가격순" },
    { value: "price_desc", label: "높은가격순" },
    { value: "discount", label: "할인율순" },
    { value: "review", label: "리뷰많은순" },
];


const DEFAULT_FILTERS = {
    categoryId: null,
    subCategoryId: null,
    brandIds: [],
    sort: "recommended",
    freeShipping: false,
    bundledShipping: false,
    priceMin: null,
    priceMax: null,
    minRating: 0,
};

/**
 * Reusable product filter component.
 *
 * @param {Array}    categories       - hierarchical categories [{ id, name, children: [{ id, name }] }]
 * @param {Array}    brands           - [{ id, name }] for brand selection in filter sheet
 * @param {number}   totalCount       - total filtered product count
 * @param {Function} onFilterChange   - called with current filters on every change
 * @param {Function} onGridViewChange - called with boolean (true = grid, false = list)
 * @param {Object}   defaultFilters   - initial filter values
 */
const ProductFilter = ({
    categories = [],
    brands = [],
    totalCount = 0,
    onFilterChange,
    onGridViewChange,
    defaultFilters = {},
}) => {
    const [applied, setApplied] = useState({ ...DEFAULT_FILTERS, ...defaultFilters });
    const [pending, setPending] = useState({ ...DEFAULT_FILTERS, ...defaultFilters });
    const [isGridView, setIsGridView] = useState(true);
    const [isSortOpen, setIsSortOpen] = useState(false);
    const [isFilterOpen, setIsFilterOpen] = useState(false);

    const updateApplied = useCallback(
        (changes) => {
            setApplied((prev) => {
                const next = { ...prev, ...changes };
                onFilterChange?.(next);
                return next;
            });
        },
        [onFilterChange]
    );

    const handleCategorySelect = (id) => {
        updateApplied({ categoryId: id, subCategoryId: null });
    };

    const handleSubCategorySelect = (id) => {
        updateApplied({ subCategoryId: id });
    };

    const handleSortSelect = (value) => {
        updateApplied({ sort: value });
        setIsSortOpen(false);
    };

    const handleGridToggle = () => {
        const next = !isGridView;
        setIsGridView(next);
        onGridViewChange?.(next);
    };

    const openFilter = () => {
        setPending({ ...applied });
        setIsFilterOpen(true);
    };

    const handleFilterApply = () => {
        setApplied(pending);
        onFilterChange?.(pending);
        setIsFilterOpen(false);
    };

    const handleFilterReset = () => {
        setPending((p) => ({
            ...p,
            brandIds: [],
            freeShipping: false,
            bundledShipping: false,
            priceMin: null,
            priceMax: null,
            minRating: 0,
        }));
    };

    const categoryTabs = useMemo(
        () => [
            { id: null, name: "전체" },
            ...categories.map((c) => ({ id: c.id, name: c.name })),
        ],
        [categories]
    );

    const activeCategoryTabIndex = useMemo(() => {
        const idx = categoryTabs.findIndex((t) => t.id === applied.categoryId);
        return idx + 1; // Tab uses 1-based index
    }, [categoryTabs, applied.categoryId]);

    const selectedCategory = useMemo(
        () => categories.find((c) => c.id === applied.categoryId),
        [categories, applied.categoryId]
    );

    const subCategories = selectedCategory?.children || [];

    const currentSortLabel =
        SORT_OPTIONS.find((o) => o.value === applied.sort)?.label ?? "추천순";

    const selectedBrandNames = brands
        .filter((b) => applied.brandIds.includes(b.id))
        .map((b) => b.name);

    const hasActiveFilters =
        applied.freeShipping ||
        applied.bundledShipping ||
        applied.priceMin !== null ||
        applied.priceMax !== null ||
        applied.minRating > 0 ||
        applied.brandIds.length > 0;

    return (
        <>
            <div className="product-filter">
                {/* ── Category tabs (대분류) ── */}
                {categories.length > 0 && (
                    <Tab
                        tabs={categoryTabs}
                        defaultActiveTab={activeCategoryTabIndex}
                        onTabChange={(id) => handleCategorySelect(id)}
                        className="product-filter__category-tabs"
                    />
                )}

                {/* ── Sub-category chips (소분류) ── */}
                {subCategories.length > 0 && (
                    <div
                        className="product-filter__sub-category-chips"
                        role="listbox"
                        aria-label="소분류"
                    >
                        <button
                            role="option"
                            aria-selected={applied.subCategoryId === null}
                            className={`sub-category-chip${applied.subCategoryId === null ? " active" : ""}`}
                            onClick={() => handleSubCategorySelect(null)}
                        >
                            전체
                        </button>
                        {subCategories.map((sub) => (
                            <button
                                key={sub.id}
                                role="option"
                                aria-selected={applied.subCategoryId === sub.id}
                                className={`sub-category-chip${applied.subCategoryId === sub.id ? " active" : ""}`}
                                onClick={() => handleSubCategorySelect(sub.id)}
                            >
                                {sub.name}
                            </button>
                        ))}
                    </div>
                )}

                {/* ── Summary bar ── */}
                <div className="product-filter__summary-bar">
                    <span className="summary-bar__count">
                        총 {totalCount.toLocaleString()}개 상품
                    </span>
                    <div className="summary-bar__actions">
                        {selectedBrandNames.length > 0 && (
                            <span className="summary-bar__brand-badge">
                                {selectedBrandNames.length === 1
                                    ? selectedBrandNames[0]
                                    : `${selectedBrandNames[0]} 외 ${selectedBrandNames.length - 1}`}
                            </span>
                        )}
                        <button
                            className="summary-bar__btn"
                            onClick={() => setIsSortOpen(true)}
                            aria-label={`정렬: ${currentSortLabel}`}
                        >
                            <SwapVertIcon style={{ fontSize: 14 }} />
                            {currentSortLabel}
                        </button>
                        <button
                            className={`summary-bar__btn${hasActiveFilters ? " summary-bar__btn--active" : ""}`}
                            onClick={openFilter}
                            aria-label="상세 필터"
                        >
                            <TuneIcon style={{ fontSize: 14 }} />
                            필터
                            {hasActiveFilters && (
                                <span className="filter-active-dot" aria-hidden="true" />
                            )}
                        </button>
                    
                    </div>
                </div>
            </div>

            {/* ── Sort bottom sheet ── */}
            <BottomSheet
                isOpen={isSortOpen}
                onClose={() => setIsSortOpen(false)}
                title="정렬"
                showHandle={true}
            >
                <ul
                    className="product-filter__sort-list"
                    role="listbox"
                    aria-label="정렬 기준"
                >
                    {SORT_OPTIONS.map((opt) => (
                        <li
                            key={opt.value}
                            role="option"
                            aria-selected={applied.sort === opt.value}
                            className={`sort-item${applied.sort === opt.value ? " active" : ""}`}
                            onClick={() => handleSortSelect(opt.value)}
                            tabIndex={0}
                            onKeyDown={(e) =>
                                e.key === "Enter" && handleSortSelect(opt.value)
                            }
                        >
                            {opt.label}
                            {applied.sort === opt.value && (
                                <span className="sort-check" aria-hidden="true">
                                    ✓
                                </span>
                            )}
                        </li>
                    ))}
                </ul>
            </BottomSheet>

            {/* ── Filter bottom sheet ── */}
            <BottomSheet
                isOpen={isFilterOpen}
                onClose={() => setIsFilterOpen(false)}
                title="필터"
                showHandle={true}
            >
                <div className="product-filter__filter-panel">
                    {/* Brand */}
                    {/* {brands.length > 0 && (
                        <div className="filter-sheet-section">
                            <h3 className="filter-sheet-section__title">브랜드</h3>
                            <div className="filter-sheet-section__chips">
                                <button
                                    className={`filter-chip${pending.brandIds.length === 0 ? " active" : ""}`}
                                    onClick={() =>
                                        setPending((p) => ({ ...p, brandIds: [] }))
                                    }
                                >
                                    전체
                                </button>
                                {brands.map((brand) => (
                                    <button
                                        key={brand.id}
                                        className={`filter-chip${pending.brandIds.includes(brand.id) ? " active" : ""}`}
                                        onClick={() =>
                                            setPending((p) => ({
                                                ...p,
                                                brandIds: p.brandIds.includes(brand.id)
                                                    ? p.brandIds.filter((id) => id !== brand.id)
                                                    : [...p.brandIds, brand.id],
                                            }))
                                        }
                                    >
                                        {brand.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )} */}

                    {/* Shipping */}
                    <div className="filter-sheet-section">
                        <h3 className="filter-sheet-section__title">배송</h3>
                        <div className="filter-sheet-section__chips">
                            <button
                                className={`filter-chip${pending.freeShipping ? " active" : ""}`}
                                onClick={() =>
                                    setPending((p) => ({ ...p, freeShipping: !p.freeShipping }))
                                }
                            >
                                무료배송
                            </button>
                            <button
                                className={`filter-chip${pending.bundledShipping ? " active" : ""}`}
                                onClick={() =>
                                    setPending((p) => ({
                                        ...p,
                                        bundledShipping: !p.bundledShipping,
                                    }))
                                }
                            >
                                묶음배송
                            </button>
                        </div>
                    </div>

                    {/* Price range */}
                    {/* <div className="filter-sheet-section">
                        <h3 className="filter-sheet-section__title">가격대</h3>
                        <div className="filter-sheet-section__chips">
                            {PRICE_RANGES.map((range) => {
                                const isActive =
                                    pending.priceMin === range.min &&
                                    pending.priceMax === range.max;
                                return (
                                    <button
                                        key={range.label}
                                        className={`filter-chip${isActive ? " active" : ""}`}
                                        onClick={() =>
                                            setPending((p) => ({
                                                ...p,
                                                priceMin: range.min,
                                                priceMax: range.max,
                                            }))
                                        }
                                    >
                                        {range.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div> */}

                    {/* Rating */}
                    {/* <div className="filter-sheet-section">
                        <h3 className="filter-sheet-section__title">평점</h3>
                        <div className="filter-sheet-section__chips">
                            {RATING_OPTIONS.map((opt) => (
                                <button
                                    key={opt.value}
                                    className={`filter-chip${pending.minRating === opt.value ? " active" : ""}`}
                                    onClick={() =>
                                        setPending((p) => ({ ...p, minRating: opt.value }))
                                    }
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div> */}

                    {/* Actions */}
                    <div className="filter-actions">
                        <button
                            className="filter-actions__reset"
                            onClick={handleFilterReset}
                        >
                            초기화
                        </button>
                        <button
                            className="filter-actions__apply"
                            onClick={handleFilterApply}
                        >
                            적용하기
                        </button>
                    </div>
                </div>
            </BottomSheet>
        </>
    );
};

export default ProductFilter;
